import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { mandateListV2 } from "../../../api/bseApi";
import {
  buildMandateListPayload,
  parseMandateListResponse,
  extractBseApiError,
} from "../../../utils/bseMandate";

const cardStyle = {
  background: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  marginBottom: "20px",
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  backgroundColor: "#fff",
  color: "#334155",
  fontSize: "14px",
};

const DEFAULT_MEMBER_ID = process.env.REACT_APP_BSE_MEMBER_ID || "63164";
const accentColor = "#06d1c6";

const STATUS_TABS = [
  { key: "all", label: "All Mandates", count: 43, icon: null },
  { key: "active", label: "Active", count: 24, icon: "fa-check", color: "#10b981" },
  { key: "pending", label: "Pending", count: 6, icon: "fa-circle", color: "#f59e0b" },
  { key: "rejected", label: "Rejected", count: 6, icon: "fa-times", color: "#ef4444" },
];

const SAMPLE_MANDATES = [
  {
    id: 1,
    name: "VIDISHA TRIVEDI",
    ucc: "CNHPT7232D",
    arn: "ARN-315999",
    account: "002401049913",
    bank: "ICICI Bank",
    startDate: "27-12-2025",
    endDate: "26-12-2065",
    umrn: "",
    status: "active",
  },
  {
    id: 2,
    name: "PRIYANKA JYOTESHKUMAR SONI",
    ucc: "EOKPS4270A",
    arn: "ARN-315999",
    account: "002401050012",
    bank: "HDFC Bank",
    startDate: "27-11-2025",
    endDate: "26-11-2065",
    umrn: "IDFB7031812251002058",
    status: "active",
  },
  {
    id: 3,
    name: "KRISHNPAL SINGH",
    ucc: "ABCPK1234L",
    arn: "ARN-315999",
    account: "002401050113",
    bank: "Yes Bank",
    startDate: "27-11-2025",
    endDate: "26-11-2065",
    umrn: "YESB7032711251001883",
    status: "active",
  },
  {
    id: 4,
    name: "SURAJ RATAN JHAWAR",
    ucc: "XYZSR5678M",
    arn: "ARN-315999",
    account: "002401050214",
    bank: "HDFC Bank",
    startDate: "27-11-2025",
    endDate: "26-11-2065",
    umrn: "HDFC7022711251032860",
    status: "active",
  },
  {
    id: 5,
    name: "VIDISHA TRIVEDI",
    ucc: "CNHPT7232D",
    arn: "ARN-315999",
    account: "002401050315",
    bank: "Kotak Mahindra Bank",
    startDate: "15-12-2025",
    endDate: "14-12-2065",
    umrn: "",
    status: "pending",
  },
  {
    id: 6,
    name: "HARVI PRANAVKUMAR SHAH",
    ucc: "DEFHP9012N",
    arn: "ARN-315999",
    account: "002401050416",
    bank: "Axis Bank",
    startDate: "01-12-2025",
    endDate: "30-11-2065",
    umrn: "UTIB7030112251003123",
    status: "active",
  },
];

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "ucc", label: "UCC" },
  { key: "arn", label: "ARN" },
  { key: "account", label: "Account" },
  { key: "bank", label: "Bank" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "umrn", label: "UMRN" },
];

/** Normalize BSE MandateDetails to table row shape */
function normalizeMandateDetails(list) {
  if (!Array.isArray(list)) return [];
  return list.map((item, idx) => {
    if (typeof item === "string") {
      const parts = item.split("|").map((p) => (p || "").trim());
      return {
        id: idx + 1,
        name: parts[1] || parts[0] || "-",
        ucc: parts[0] || parts[2] || "-",
        arn: "",
        account: parts[4] || "-",
        bank: parts[5] || "-",
        startDate: parts[6] || "-",
        endDate: parts[7] || "-",
        umrn: parts[8] || "-",
        status: "active",
      };
    }
    return {
      id: item.MandateId || item.mandateId || idx + 1,
      name: item.ClientName || item.clientName || item.Name || "-",
      ucc: item.ClientCode || item.clientCode || item.UCC || "-",
      arn: item.ARN || item.arn || "",
      account: item.AccountNo || item.accountNo || item.Account || "-",
      bank: item.BankName || item.bankName || item.Bank || "-",
      startDate: item.StartDate || item.startDate || "-",
      endDate: item.EndDate || item.endDate || "-",
      umrn: item.UMRN || item.umrn || item.MandateId || "-",
      status: (item.Status || item.status || "active").toLowerCase(),
    };
  });
}

export default function MandateList() {
  const [searchName, setSearchName] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRowId, setSelectedRowId] = useState(1);
  const [sortBy, setSortBy] = useState("startDate");
  const [sortDir, setSortDir] = useState("desc");
  const fileInputRef = React.useRef(null);
  const [fromDate, setFromDate] = useState("01/01/2024");
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  });
  const [mandatesFromApi, setMandatesFromApi] = useState([]);
  const [loadingMandates, setLoadingMandates] = useState(false);
  const [apiError, setApiError] = useState(null);

  const fetchMandates = useCallback(async () => {
    setLoadingMandates(true);
    setApiError(null);
    const ucc = searchName.trim();
    try {
      const res = await mandateListV2(
        buildMandateListPayload({
          memberId: DEFAULT_MEMBER_ID,
          clientCode: /^[A-Z0-9@._-]{3,}$/i.test(ucc) ? ucc : "",
        })
      );
      const body = res?.data || {};
      if (body.status === "success") {
        const rows = parseMandateListResponse(body);
        setMandatesFromApi(rows);
        if (!rows.length) {
          setApiError(null);
        }
        return;
      }
      throw new Error(extractBseApiError({ response: { data: body } }));
    } catch (err) {
      setMandatesFromApi([]);
      setApiError(extractBseApiError(err));
    } finally {
      setLoadingMandates(false);
    }
  }, [searchName]);

  useEffect(() => {
    fetchMandates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayList = mandatesFromApi.length > 0 ? mandatesFromApi : SAMPLE_MANDATES;
  const filteredMandates = displayList.filter((row) => {
    const search = searchName.toLowerCase();
    const nameMatch = (row.name || "").toLowerCase().includes(search);
    const uccMatch = (row.ucc || "").toLowerCase().includes(search);
    if (statusFilter === "all") return nameMatch || uccMatch;
    return (nameMatch || uccMatch) && row.status === statusFilter;
  });

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ columnKey }) => (
    <button
      type="button"
      className="btn btn-link p-0 ms-1 border-0"
      style={{
        color: sortBy === columnKey ? accentColor : "#64748b",
        fontSize: "11px",
      }}
      onClick={() => toggleSort(columnKey)}
      aria-label={`Sort by ${columnKey}`}
    >
      <i
        className={`fa fa-sort-${sortBy === columnKey ? (sortDir === "asc" ? "up" : "down") : "up"}`}
        style={{ opacity: sortBy === columnKey ? 1 : 0.4 }}
      />
    </button>
  );

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          {/* Breadcrumb and Header */}
          <div className="mb-4" style={cardStyle}>
            <div className="content-page-header content-page-headersplit">
              <h5>Mandate List</h5>
            </div>
            <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/dashboard">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <NavLink to="/admin/invest-bse/profile-list">Invest Online BSE</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Mandate List
                </li>
              </ol>
            </nav>
          </div>

          {/* Search Mandates - Top section */}
          <div style={cardStyle}>
            <h6 className="mb-3" style={{ fontWeight: 700, color: "#334155", fontSize: "16px" }}>
              Search Mandates
            </h6>
            <div className="d-flex flex-wrap align-items-end justify-content-between gap-3">
              <div style={{ maxWidth: 400, flex: "1 1 300px" }}>
                <label className="form-label small text-secondary mb-1 d-block">Search Name and UCC</label>
                <input
                  type="text"
                  placeholder="Search Name and UCC"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  style={inputStyle}
                />
              </div>
              {apiError && (
                <div className="small text-danger mb-2" style={{ flexBasis: "100%" }}>
                  {apiError}
                </div>
              )}
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span style={{ fontSize: "14px", color: "#475569", marginRight: 4 }}>Upload:</span>
                <button
                  type="button"
                  className="btn btn-link p-0 d-inline-flex align-items-center gap-1 text-decoration-none"
                  style={{ color: "#475569", fontSize: "14px" }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <i className="fa fa-upload" />
                  Mandate List
                </button>
                <input ref={fileInputRef} type="file" accept=".xls,.xlsx,.csv" style={{ display: "none" }} onChange={() => {}} />
                <button
                  type="button"
                  onClick={fetchMandates}
                  disabled={loadingMandates}
                  className="d-flex align-items-center gap-2 btn btn-sm"
                  style={{
                    color: "#10b981",
                    background: "#fff",
                    border: "1px solid #10b981",
                    borderRadius: "6px",
                    padding: "8px 14px",
                    fontWeight: 600,
                    opacity: loadingMandates ? 0.7 : 1,
                  }}
                >
                  {loadingMandates ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      Loading…
                    </>
                  ) : (
                    <>
                      <i className="fa fa-sync-alt" />
                      Sync
                    </>
                  )}
                </button>
                <NavLink
                  to="/admin/invest-bse/create-mandate"
                  className="d-flex align-items-center gap-2 btn btn-sm text-decoration-none"
                  style={{
                    color: "#fff",
                    background: "#10b981",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontWeight: 600,
                  }}
                >
                  Register Mandate
                </NavLink>
              </div>
            </div>
          </div>

          {/* Status tabs */}
          <div
            className="d-flex flex-wrap gap-2 mb-3"
            style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 12 }}
            role="tablist"
          >
            {STATUS_TABS.map(({ key, label, count, icon, color }) => {
              const isActive = statusFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setStatusFilter(key)}
                  className="d-flex align-items-center gap-2 border-0 bg-transparent p-0"
                  style={{
                    paddingBottom: 8,
                    marginBottom: -13,
                    borderBottom: isActive ? "2px solid #3b82f6" : "2px solid transparent",
                    fontSize: "14px",
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "#334155" : "#64748b",
                    cursor: "pointer",
                  }}
                >
                  {icon && (
                    <i
                      className={`fa ${icon}`}
                      style={{
                        fontSize: "12px",
                        color: color || "#64748b",
                      }}
                    />
                  )}
                  <span>{label}</span>
                  {key === "all" ? (
                    <span>: {count}</span>
                  ) : (
                    <span>: {count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Table */}
          <div style={{ ...cardStyle, overflowX: "auto", padding: 0 }}>
            <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <table className="table table-bordered mb-0" style={{ fontSize: "13px" }}>
                <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
                  <tr>
                    {COLUMNS.map(({ key, label }) => (
                      <th
                        key={key}
                        className="text-nowrap"
                        style={{
                          padding: "10px 12px",
                          fontWeight: 600,
                          color: "#334155",
                          borderBottom:
                            sortBy === key && key === "startDate"
                              ? `2px solid ${accentColor}`
                              : undefined,
                        }}
                      >
                        {label}
                        <SortIcon columnKey={key} />
                      </th>
                    ))}
                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#334155", width: 60 }}>
                      Actions
                      <i className="fa fa-ellipsis-v ms-1" style={{ fontSize: "11px", color: "#94a3b8" }} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMandates.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedRowId(row.id)}
                      style={{
                        cursor: "pointer",
                        background:
                          selectedRowId === row.id ? "rgba(6, 209, 198, 0.12)" : undefined,
                      }}
                    >
                      <td style={{ padding: "10px 12px" }}>{row.name}</td>
                      <td style={{ padding: "10px 12px", color: accentColor, fontWeight: 500 }}>
                        {row.ucc}
                      </td>
                      <td style={{ padding: "10px 12px" }}>{row.arn}</td>
                      <td style={{ padding: "10px 12px" }}>{row.account}</td>
                      <td style={{ padding: "10px 12px" }}>{row.bank}</td>
                      <td style={{ padding: "10px 12px" }}>{row.startDate}</td>
                      <td style={{ padding: "10px 12px" }}>{row.endDate}</td>
                      <td style={{ padding: "10px 12px", color: row.umrn ? "inherit" : "#94a3b8" }}>
                        {row.umrn || "-"}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-secondary"
                          aria-label="Actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="fa fa-ellipsis-v" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
